// messages.ts — language-free engine messages.
//
// The engine never writes user-facing text. Wherever it has to tell the user something (a blocker, an
// alert, a missing field) it returns a MESSAGE KEY plus parameters, and the display layer translates the
// key (French lives in `fr.json`, under the very same key).
//
// A message that has to travel inside a `string[]` (blockers, alerts, notes: persisted in frozen quote
// results and passed verbatim by many consumers) is encoded as `key` or `key|{"param":value}`. A string
// that is not in that form (a legacy frozen result holding French text) is left untouched by
// {@link resolveEngineMessage}, so old snapshots keep displaying as they were stored.

/** Parameters of a message: scalars substituted into the translation. */
export type EngineMessageParams = Readonly<Record<string, string | number>>;

/** A decoded message. */
export interface DecodedEngineMessage {
  key: string;
  params: EngineMessageParams;
}

/** Every engine message key starts with this prefix, which is how an encoded message is recognised. */
export const ENGINE_MESSAGE_PREFIX = "engine";

/**
 * Prefix marking a parameter value that is itself a message to translate (a field name, a nested reason),
 * so a parameter can be a translated word without the engine knowing any language.
 */
export const KEY_PARAM_PREFIX = "$";

const ENCODED = /^(engine[A-Za-z0-9]+)(?:\|(\{.*\}))?$/s;

/**
 * Encodes a message for transport inside a `string[]`.
 * @param key - Message key (starts with `engine`, same key as in fr.json).
 * @param params - Optional scalar parameters. A value starting with `$` is a key translated by the display layer.
 * @returns The encoded message.
 */
export function engineMessage(key: string, params?: EngineMessageParams): string {
  return params && Object.keys(params).length > 0 ? `${key}|${JSON.stringify(params)}` : key;
}

/**
 * Reference to a message as a parameter value (translated by the display layer). Several keys give a list,
 * joined by ", " once translated.
 * @param keys - Message key(s) to translate.
 * @returns The marked value.
 */
export function keyParam(...keys: string[]): string {
  return keys.map((key) => KEY_PARAM_PREFIX + key).join(" ");
}

/**
 * A whole encoded message as a parameter value (nested reason), translated by the display layer.
 * @param encoded - Message built by {@link engineMessage}.
 * @returns The marked value.
 */
export function messageParam(encoded: string): string {
  return KEY_PARAM_PREFIX + encoded;
}

/**
 * A list of messages (or plain texts) as a parameter value, translated item by item and joined by ", ".
 * @param items - Encoded messages ({@link engineMessage}) or plain texts, kept as they are.
 * @returns The marked value.
 */
export function messageListParam(items: readonly string[]): string {
  return `${KEY_PARAM_PREFIX}${JSON.stringify(items)}`;
}

/**
 * Decodes an encoded message.
 * @param text - Candidate string.
 * @returns The key and parameters, or `null` when the string is not an encoded engine message.
 */
export function decodeEngineMessage(text: string): DecodedEngineMessage | null {
  const match = ENCODED.exec(text);
  if (!match) return null;
  if (!match[2]) return { key: match[1]!, params: {} };
  try {
    return { key: match[1]!, params: JSON.parse(match[2]) as EngineMessageParams };
  } catch {
    return null;
  }
}

/**
 * Translates an engine message string with the given translator; any string that is not an encoded
 * message (legacy text) is returned unchanged. Numeric parameters are formatted for the locale; a number
 * whose name ends with `Eur` is formatted as euros, with `Pct` as a percentage value (one decimal, no sign).
 * @param text - Encoded message or legacy text.
 * @param translate - Key + params to text (e.g. `TranslateService.instant`).
 * @param locale - BCP 47 locale used to format numbers (e.g. the active language); undefined = runtime default.
 * @returns The displayable text.
 */
export function resolveEngineMessage(
  text: string,
  translate: (key: string, params?: Record<string, string | number>) => string,
  locale: string | undefined,
): string {
  const decoded = decodeEngineMessage(text);
  if (!decoded) return text;
  const params: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(decoded.params)) {
    if (typeof value === "number") {
      const options: Intl.NumberFormatOptions = name.endsWith("Eur")
        ? { style: "currency", currency: "EUR" }
        : name.endsWith("Pct")
          ? { maximumFractionDigits: 1 }
          : name.endsWith("Dec2")
            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            : name.endsWith("Dec3")
              ? { minimumFractionDigits: 3, maximumFractionDigits: 3 }
              : { maximumFractionDigits: 3 };
      params[name] = new Intl.NumberFormat(locale, options).format(value);
    } else if (!value.startsWith(KEY_PARAM_PREFIX)) {
      params[name] = value;
    } else if (value.startsWith("$[")) {
      params[name] = (JSON.parse(value.slice(1)) as string[]).map((item) => resolveEngineMessage(item, translate, locale)).join(", ");
    } else if (/^(\$engine[A-Za-z0-9]+)( \$engine[A-Za-z0-9]+)*$/.test(value)) {
      params[name] = value.split(" ").map((k) => translate(k.slice(1))).join(", ");
    } else {
      params[name] = resolveEngineMessage(value.slice(1), translate, locale);
    }
  }
  return translate(decoded.key, params);
}

/**
 * Joiner used by Sellsy print titles (`qty · print · material · …`). Each segment may be an
 * encoded engine message or plain catalogue text; {@link resolveEngineText} resolves them one by one.
 */
export const ENGINE_TEXT_SEGMENT_SEP = " · ";

/**
 * Resolves a possibly composite block: each line (and each ` · ` segment within a line) is run
 * through {@link resolveEngineMessage}. Used for production notes, Sellsy titles (encoded parts
 * joined by {@link ENGINE_TEXT_SEGMENT_SEP}) and Sellsy description blocks.
 * @param text - Encoded message(s), bare keys, or legacy plain text (possibly multi-line / segmented).
 * @param translate - Key + params to text.
 * @param locale - BCP 47 locale for number formatting.
 * @returns The displayable text.
 */
export function resolveEngineText(
  text: string,
  translate: (key: string, params?: Record<string, string | number>) => string,
  locale: string | undefined,
): string {
  const resolveLine = (line: string): string => {
    if (!line.includes(ENGINE_TEXT_SEGMENT_SEP)) {
      return resolveEngineMessage(line, translate, locale);
    }
    return line
      .split(ENGINE_TEXT_SEGMENT_SEP)
      .map((part) => resolveEngineMessage(part, translate, locale))
      .join(ENGINE_TEXT_SEGMENT_SEP);
  };
  if (!text.includes("\n")) return resolveLine(text);
  return text.split("\n").map(resolveLine).join("\n");
}
